import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { storage } from '../utils/storage'
import { Platform } from '../utils/platform'
import { supabase } from '../services/supabase'

// Get the appropriate storage implementation
const getStorage = () => {
  if (Platform.isNative && (global as any).__FLOWS_STORAGE__) {
    return (global as any).__FLOWS_STORAGE__;
  }
  return storage;
};

export interface Tag {
  id: string
  user_id: string
  name: string
  color: string
  total_transactions: number
  created_at: string
  updated_at: string
}

interface TagsState {
  tags: Tag[]
  isLoading: boolean
  error: string | null
  lastFetchTime: number | null
  fetchTags: (userId: string, force?: boolean) => Promise<void>
  addTag: (data: Omit<Tag, 'id' | 'created_at' | 'updated_at' | 'total_transactions'>) => Promise<void>
  updateTag: (tagId: string, data: Partial<Omit<Tag, 'id' | 'user_id' | 'created_at' | 'updated_at' | 'total_transactions'>>) => Promise<void>
  deleteTag: (userId: string, tagId: string) => Promise<void>
  isTagNameExists: (userId: string, name: string, excludeId?: string) => Promise<boolean>
}

export const useTagsStore = create<TagsState>()(
  persist(
    (set, get) => ({
      tags: [],
      isLoading: false,
      error: null,
      lastFetchTime: null,

      fetchTags: async (userId: string, force: boolean = false) => {
        if (!userId) return
        
        // Check if we have recent data (within last 5 minutes) to avoid unnecessary fetches
        // Skip the cache check if force=true
        if (!force) {
          const now = Date.now();
          const fiveMinutesAgo = now - 5 * 60 * 1000;
          const lastFetch = get().lastFetchTime;
          
          if (lastFetch && lastFetch > fiveMinutesAgo && get().tags.length > 0) {
            console.log('Using cached tags data, last fetched at:', new Date(lastFetch).toLocaleTimeString());
            return;
          }
        } else {
          console.log('Force refreshing tags data');
        }
        
        set({ isLoading: true, error: null })
        try {
          const { data: tags, error } = await supabase
            .from('tags')
            .select('*')
            .eq('user_id', userId)
            .order('name')

          if (error) throw error
          
          set({ 
            tags: tags || [], 
            isLoading: false,
            lastFetchTime: Date.now()
          })
        } catch (error) {
          console.error('Error fetching tags:', error)
          set({ 
            error: error instanceof Error ? error.message : 'Failed to fetch tags',
            isLoading: false 
          })
        }
      },

      isTagNameExists: async (userId: string, name: string, excludeId?: string) => {
        try {
          const { data: tags, error } = await supabase
            .from('tags')
            .select('id')
            .eq('user_id', userId)
            .eq('name', name.trim());

          if (error) throw error;

          if (excludeId) {
            return (tags || []).some(t => t.id !== excludeId);
          }

          return (tags || []).length > 0;
        } catch (error) {
          console.error('Error checking tag name:', error);
          return false;
        }
      },

      addTag: async (data) => {
        if (!data.user_id) return
        
        set({ isLoading: true, error: null })
        try {
          // Check if name exists
          const exists = await get().isTagNameExists(data.user_id, data.name);
          if (exists) {
            throw new Error('A tag with this name already exists');
          }
          
          const now = new Date().toISOString()
          const newTag = {
            ...data,
            total_transactions: 0,
            created_at: now,
            updated_at: now
          }
          
          const { data: tag, error } = await supabase
            .from('tags')
            .insert([newTag])
            .select()
            .single()

          if (error) throw error
          
          set(state => ({
            tags: [tag, ...state.tags].sort((a, b) => a.name.localeCompare(b.name)),
            isLoading: false
          }))
        } catch (error) {
          console.error('Error adding tag:', error)
          set({ 
            error: error instanceof Error ? error.message : 'Failed to add tag',
            isLoading: false 
          })
          throw error
        }
      },

      updateTag: async (tagId, data) => {
        if (!tagId) return
        
        set({ isLoading: true, error: null })
        try {
          const now = new Date().toISOString()
          const updates = {
            ...data,
            updated_at: now
          }
          
          const { data: updatedTag, error } = await supabase
            .from('tags')
            .update(updates)
            .eq('id', tagId)
            .select()
            .single()

          if (error) throw error
          
          set(state => ({
            tags: state.tags.map(tag =>
              tag.id === tagId ? { ...tag, ...updatedTag } : tag
            ),
            isLoading: false
          }))
        } catch (error) {
          console.error('Error updating tag:', error)
          set({ 
            error: error instanceof Error ? error.message : 'Failed to update tag',
            isLoading: false 
          })
          throw error
        }
      },

      deleteTag: async (userId, tagId) => {
        if (!userId || !tagId) return
        
        set({ isLoading: true, error: null })
        try {
          const { error } = await supabase
            .from('tags')
            .delete()
            .eq('id', tagId)
            .eq('user_id', userId)

          if (error) throw error
          
          set(state => ({
            tags: state.tags.filter(t => t.id !== tagId),
            isLoading: false
          }))
        } catch (error) {
          console.error('Error deleting tag:', error)
          set({ 
            error: error instanceof Error ? error.message : 'Failed to delete tag',
            isLoading: false 
          })
          throw error
        }
      }
    }),
    {
      name: 'flows-tags',
      storage: createJSONStorage(() => getStorage())
    }
  )
) 